import React, { useState, useEffect } from 'react';
import './Admin.css';
import axios from 'axios';

// Login form component for admin authentication
function AdminLogin({ onLogin }) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/admin/login`,
        { password }
      );

      if (response.data.authenticated) {
        // Store the token in localStorage
        localStorage.setItem('adminToken', response.data.token);
        localStorage.setItem('adminTokenExpiry', response.data.expires);

        // Configure axios to use the token for future requests
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.token}`;

        // Clear the password field for security
        setPassword('');
        
        // Notify parent component about successful login
        onLogin(true);
      } else {
        setError('Authentication failed. Invalid credentials.');
      }
    } catch (err) {
      console.error('Login error:', err);
      
      // Provide more specific error messages
      if (err.response) {
        if (err.response.status === 401) {
          setError('Incorrect password. Please try again.');
        } else if (err.response.status === 429) {
          setError('Too many login attempts. Please try again later.');
        } else {
          setError(err.response.data?.detail || 'Login failed. Please try again.');
        }
      } else if (err.request) {
        setError('Network error. Please check your connection and try again.');
      } else {
        setError('Login failed. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="admin-login-container">
      <h1>Admin Login</h1>
      {error && <div className="login-error">{error}</div>}
      <form onSubmit={handleSubmit} className="login-form">
        <div className="form-group">
          <label htmlFor="password">Password</label>
          <input
            type="password"
            id="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            required
          />
        </div>
        <button type="submit" disabled={isLoading}>
          {isLoading ? 'Logging in...' : 'Login'}
        </button>
      </form>
    </div>
  );
}

const CollapsibleSection = ({ title, children }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="collapsible-section">
      <div className="section-title" onClick={() => setIsOpen(!isOpen)}>
        <h2>{title}</h2>
        <button className="collapse-button">
          {isOpen ? '▼' : '▶'}
        </button>
      </div>
      <div className={`section-content ${isOpen ? 'open' : 'closed'}`}>
        {children}
      </div>
    </div>
  );
};

function HomeEditor({ data, onUpdate }) {
  if (!data) return <div>Loading home data...</div>;

  const updateHero = (field, value) => {
    onUpdate({
      ...data,
      hero: { ...data.hero, [field]: value }
    });
  };

  const updateService = (index, field, value) => {
    const newServices = [...data.services];
    newServices[index] = { ...newServices[index], [field]: value };
    onUpdate({ ...data, services: newServices });
  };

  const updateFeature = (index, field, value) => {
    const newFeatures = [...data.features];
    newFeatures[index] = { ...newFeatures[index], [field]: value };
    onUpdate({ ...data, features: newFeatures });
  };

  const updateContact = (field, value) => {
    onUpdate({
      ...data,
      contact: { ...data.contact, [field]: value }
    });
  };

  return (
    <div className="content-editor">
      <CollapsibleSection title="Hero Section">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.hero.title}
            onChange={(e) => updateHero('title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <textarea
            value={data.hero.subtitle}
            onChange={(e) => updateHero('subtitle', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Services">
        {data.services.map((service, index) => (
          <div key={index} className="form-group service-item">
            <div className="form-row">
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={service.title}
                  onChange={(e) => updateService(index, 'title', e.target.value)}
                />
              </div>
              <div className="form-group">
                <label>Link</label>
                <input
                  type="text"
                  value={service.link}
                  onChange={(e) => updateService(index, 'link', e.target.value)}
                />
              </div>
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={service.description}
                onChange={(e) => updateService(index, 'description', e.target.value)}
              />
            </div>
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection title="Features">
        {data.features.map((feature, index) => (
          <div key={index} className="form-group feature-item">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={feature.title}
                onChange={(e) => updateFeature(index, 'title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={feature.description}
                onChange={(e) => updateFeature(index, 'description', e.target.value)}
              />
            </div>
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection title="Contact Information">
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={data.contact.email}
            onChange={(e) => updateContact('email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="text"
            value={data.contact.phone}
            onChange={(e) => updateContact('phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Location</label>
          <input
            type="text"
            value={data.contact.location}
            onChange={(e) => updateContact('location', e.target.value)}
          />
        </div>
      </CollapsibleSection>
    </div>
  );
}

function AccountingEditor({ data, onUpdate }) {
  if (!data) return <div>Loading accounting data...</div>;

  const updateHero = (field, value) => {
    onUpdate({
      ...data,
      hero: { ...data.hero, [field]: value }
    });
  };

  const updateService = (category, type, field, value) => {
    const newServices = {
      ...data.services,
      [category]: {
        ...data.services[category],
        [type]: {
          ...data.services[category][type],
          [field]: field === 'features' ? value.split('\\n') : value
        }
      }
    };
    onUpdate({ ...data, services: newServices });
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...data.benefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    onUpdate({ ...data, benefits: newBenefits });
  };

  return (
    <div className="content-editor">
      <CollapsibleSection title="Hero Section">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.hero.title}
            onChange={(e) => updateHero('title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <textarea
            value={data.hero.subtitle}
            onChange={(e) => updateHero('subtitle', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Services">
        {Object.entries(data.services).map(([category, plans]) => (
          <div key={category} className="service-category">
            <h4>{category.charAt(0).toUpperCase() + category.slice(1)}</h4>
            {Object.entries(plans).map(([type, plan]) => (
              <div key={type} className="plan-item">
                <h5>{type.charAt(0).toUpperCase() + type.slice(1)} Plan</h5>
                <div className="form-group">
                  <label>Title</label>
                  <input
                    type="text"
                    value={plan.title}
                    onChange={(e) => updateService(category, type, 'title', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Price</label>
                  <input
                    type="text"
                    value={plan.price}
                    onChange={(e) => updateService(category, type, 'price', e.target.value)}
                  />
                </div>
                <div className="form-group">
                  <label>Features (one per line)</label>
                  <textarea
                    value={plan.features.join('\\n')}
                    onChange={(e) => updateService(category, type, 'features', e.target.value)}
                  />
                </div>
              </div>
            ))}
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection title="Benefits">
        {data.benefits.map((benefit, index) => (
          <div key={index} className="form-group benefit-item">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={benefit.title}
                onChange={(e) => updateBenefit(index, 'title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={benefit.description}
                onChange={(e) => updateBenefit(index, 'description', e.target.value)}
              />
            </div>
          </div>
        ))}
      </CollapsibleSection>
    </div>
  );
}

function RegistrationEditor({ data, onUpdate }) {
  if (!data) return <div>Loading registration data...</div>;

  const updateHero = (field, value) => {
    onUpdate({
      ...data,
      hero: { ...data.hero, [field]: value }
    });
  };

  const updateService = (index, field, value) => {
    const newServices = [...data.services];
    newServices[index] = {
      ...newServices[index],
      [field]: field === 'features' ? value.split('\\n') : value
    };
    onUpdate({ ...data, services: newServices });
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...data.benefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    onUpdate({ ...data, benefits: newBenefits });
  };

  return (
    <div className="content-editor">
      <CollapsibleSection title="Hero Section">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.hero.title}
            onChange={(e) => updateHero('title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <textarea
            value={data.hero.subtitle}
            onChange={(e) => updateHero('subtitle', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Services">
        {data.services.map((service, index) => (
          <div key={index} className="form-group service-item">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={service.title}
                onChange={(e) => updateService(index, 'title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={service.description}
                onChange={(e) => updateService(index, 'description', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Price</label>
              <input
                type="text"
                value={service.price}
                onChange={(e) => updateService(index, 'price', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Features (one per line)</label>
              <textarea
                value={service.features.join('\\n')}
                onChange={(e) => updateService(index, 'features', e.target.value)}
              />
            </div>
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection title="Benefits">
        {data.benefits.map((benefit, index) => (
          <div key={index} className="form-group benefit-item">
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={benefit.title}
                onChange={(e) => updateBenefit(index, 'title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={benefit.description}
                onChange={(e) => updateBenefit(index, 'description', e.target.value)}
              />
            </div>
          </div>
        ))}
      </CollapsibleSection>
    </div>
  );
}

const AboutEditor = ({ data, onUpdate }) => {
  if (!data) return <div>Loading about data...</div>;

  return (
    <div className="editor-section">
      <CollapsibleSection title="About Page Content">
        <div className="section-content">
          <h3>Hero Section</h3>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.hero.title}
              onChange={(e) => onUpdate({
                ...data,
                hero: { ...data.hero, title: e.target.value }
              })}
            />
          </div>
          <div className="form-group">
            <label>Subtitle</label>
            <input
              type="text"
              value={data.hero.subtitle}
              onChange={(e) => onUpdate({
                ...data,
                hero: { ...data.hero, subtitle: e.target.value }
              })}
            />
          </div>

          <h3>Our Story</h3>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.story.title}
              onChange={(e) => onUpdate({
                ...data,
                story: { ...data.story, title: e.target.value }
              })}
            />
          </div>
          <div className="form-group">
            <label>Story Content</label>
            <textarea
              value={data.story.content}
              onChange={(e) => onUpdate({
                ...data,
                story: { ...data.story, content: e.target.value }
              })}
              rows="4"
            />
          </div>

          <h3>Our Mission</h3>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.mission.title}
              onChange={(e) => onUpdate({
                ...data,
                mission: { ...data.mission, title: e.target.value }
              })}
            />
          </div>
          <div className="form-group">
            <label>Mission Content</label>
            <textarea
              value={data.mission.content}
              onChange={(e) => onUpdate({
                ...data,
                mission: { ...data.mission, content: e.target.value }
              })}
              rows="4"
            />
          </div>

          <h3>Our Values</h3>
          {data.values.map((value, index) => (
            <div key={index} className="form-group">
              <label>Value {index + 1}</label>
              <input
                type="text"
                value={value.title}
                onChange={(e) => {
                  const newValues = [...data.values];
                  newValues[index] = {
                    ...newValues[index],
                    title: e.target.value
                  };
                  onUpdate({ ...data, values: newValues });
                }}
              />
              <textarea
                value={value.description}
                onChange={(e) => {
                  const newValues = [...data.values];
                  newValues[index] = {
                    ...newValues[index],
                    description: e.target.value
                  };
                  onUpdate({ ...data, values: newValues });
                }}
                rows="3"
              />
            </div>
          ))}

          <h3>Team Members</h3>
          {data.team.map((member, index) => (
            <div key={index} className="form-group">
              <label>Team Member {index + 1}</label>
              <input
                type="text"
                placeholder="Name"
                value={member.name}
                onChange={(e) => {
                  const newTeam = [...data.team];
                  newTeam[index] = {
                    ...newTeam[index],
                    name: e.target.value
                  };
                  onUpdate({ ...data, team: newTeam });
                }}
              />
              <input
                type="text"
                placeholder="Role"
                value={member.role}
                onChange={(e) => {
                  const newTeam = [...data.team];
                  newTeam[index] = {
                    ...newTeam[index],
                    role: e.target.value
                  };
                  onUpdate({ ...data, team: newTeam });
                }}
              />
              <textarea
                placeholder="Bio"
                value={member.bio}
                onChange={(e) => {
                  const newTeam = [...data.team];
                  newTeam[index] = {
                    ...newTeam[index],
                    bio: e.target.value
                  };
                  onUpdate({ ...data, team: newTeam });
                }}
                rows="3"
              />
            </div>
          ))}
        </div>
      </CollapsibleSection>
    </div>
  );
};

const ContactEditor = ({ data, onUpdate }) => {
  if (!data) return <div>Loading contact data...</div>;

  return (
    <div className="editor-section">
      <CollapsibleSection title="Contact Page Content">
        <div className="section-content">
          <h3>Hero Section</h3>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.hero.title}
              onChange={(e) => onUpdate({
                ...data,
                hero: { ...data.hero, title: e.target.value }
              })}
            />
          </div>
          <div className="form-group">
            <label>Subtitle</label>
            <input
              type="text"
              value={data.hero.subtitle}
              onChange={(e) => onUpdate({
                ...data,
                hero: { ...data.hero, subtitle: e.target.value }
              })}
            />
          </div>

          <h3>Contact Information</h3>
          <div className="form-group">
            <label>Address</label>
            <input
              type="text"
              placeholder="Street"
              value={data.contact_info.address.street}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  address: {
                    ...data.contact_info.address,
                    street: e.target.value
                  }
                }
              })}
            />
            <input
              type="text"
              placeholder="City"
              value={data.contact_info.address.city}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  address: {
                    ...data.contact_info.address,
                    city: e.target.value
                  }
                }
              })}
            />
            <input
              type="text"
              placeholder="State"
              value={data.contact_info.address.state}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  address: {
                    ...data.contact_info.address,
                    state: e.target.value
                  }
                }
              })}
            />
            <input
              type="text"
              placeholder="Pincode"
              value={data.contact_info.address.pincode}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  address: {
                    ...data.contact_info.address,
                    pincode: e.target.value
                  }
                }
              })}
            />
          </div>

          <div className="form-group">
            <label>Phone Numbers</label>
            <input
              type="text"
              placeholder="Main"
              value={data.contact_info.phone.main}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  phone: {
                    ...data.contact_info.phone,
                    main: e.target.value
                  }
                }
              })}
            />
            <input
              type="text"
              placeholder="Support"
              value={data.contact_info.phone.support}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  phone: {
                    ...data.contact_info.phone,
                    support: e.target.value
                  }
                }
              })}
            />
          </div>

          <div className="form-group">
            <label>Email Addresses</label>
            <input
              type="email"
              placeholder="General"
              value={data.contact_info.email.general}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  email: {
                    ...data.contact_info.email,
                    general: e.target.value
                  }
                }
              })}
            />
            <input
              type="email"
              placeholder="Support"
              value={data.contact_info.email.support}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  email: {
                    ...data.contact_info.email,
                    support: e.target.value
                  }
                }
              })}
            />
            <input
              type="email"
              placeholder="Sales"
              value={data.contact_info.email.sales}
              onChange={(e) => onUpdate({
                ...data,
                contact_info: {
                  ...data.contact_info,
                  email: {
                    ...data.contact_info.email,
                    sales: e.target.value
                  }
                }
              })}
            />
          </div>

          <h3>Office Hours</h3>
          <div className="form-group">
            <label>Weekdays</label>
            <input
              type="text"
              value={data.office_hours.weekdays}
              onChange={(e) => onUpdate({
                ...data,
                office_hours: {
                  ...data.office_hours,
                  weekdays: e.target.value
                }
              })}
            />
          </div>
          <div className="form-group">
            <label>Saturday</label>
            <input
              type="text"
              value={data.office_hours.saturday}
              onChange={(e) => onUpdate({
                ...data,
                office_hours: {
                  ...data.office_hours,
                  saturday: e.target.value
                }
              })}
            />
          </div>
          <div className="form-group">
            <label>Sunday</label>
            <input
              type="text"
              value={data.office_hours.sunday}
              onChange={(e) => onUpdate({
                ...data,
                office_hours: {
                  ...data.office_hours,
                  sunday: e.target.value
                }
              })}
            />
          </div>

          <h3>Social Media</h3>
          <div className="form-group">
            <label>LinkedIn</label>
            <input
              type="url"
              value={data.social_media.linkedin}
              onChange={(e) => onUpdate({
                ...data,
                social_media: {
                  ...data.social_media,
                  linkedin: e.target.value
                }
              })}
            />
          </div>
          <div className="form-group">
            <label>Twitter</label>
            <input
              type="url"
              value={data.social_media.twitter}
              onChange={(e) => onUpdate({
                ...data,
                social_media: {
                  ...data.social_media,
                  twitter: e.target.value
                }
              })}
            />
          </div>
          <div className="form-group">
            <label>Facebook</label>
            <input
              type="url"
              value={data.social_media.facebook}
              onChange={(e) => onUpdate({
                ...data,
                social_media: {
                  ...data.social_media,
                  facebook: e.target.value
                }
              })}
            />
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

const TaxEditor = ({ data, onUpdate }) => {
  if (!data) return <div>Loading tax data...</div>;

  return (
    <div className="editor-section">
      <CollapsibleSection title="Tax Page Content">
        <div className="section-content">
          <h3>Hero Section</h3>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.hero.title}
              onChange={(e) => onUpdate({
                ...data,
                hero: { ...data.hero, title: e.target.value }
              })}
            />
          </div>
          <div className="form-group">
            <label>Subtitle</label>
            <input
              type="text"
              value={data.hero.subtitle}
              onChange={(e) => onUpdate({
                ...data,
                hero: { ...data.hero, subtitle: e.target.value }
              })}
            />
          </div>

          <h3>Tax Services</h3>
          {data.services.map((service, index) => (
            <div key={index} className="service-item">
              <h4>Service {index + 1}</h4>
              <div className="form-group">
                <label>Title</label>
                <input
                  type="text"
                  value={service.title}
                  onChange={(e) => {
                    const newServices = [...data.services];
                    newServices[index] = {
                      ...newServices[index],
                      title: e.target.value
                    };
                    onUpdate({ ...data, services: newServices });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Description</label>
                <textarea
                  value={service.description}
                  onChange={(e) => {
                    const newServices = [...data.services];
                    newServices[index] = {
                      ...newServices[index],
                      description: e.target.value
                    };
                    onUpdate({ ...data, services: newServices });
                  }}
                  rows="3"
                />
              </div>
              <div className="form-group">
                <label>Price</label>
                <input
                  type="text"
                  value={service.price}
                  onChange={(e) => {
                    const newServices = [...data.services];
                    newServices[index] = {
                      ...newServices[index],
                      price: e.target.value
                    };
                    onUpdate({ ...data, services: newServices });
                  }}
                />
              </div>
              <div className="form-group">
                <label>Features (one per line)</label>
                <textarea
                  value={service.features.join('\n')}
                  onChange={(e) => {
                    const newServices = [...data.services];
                    newServices[index] = {
                      ...newServices[index],
                      features: e.target.value.split('\n').filter(feature => feature.trim() !== '')
                    };
                    onUpdate({ ...data, services: newServices });
                  }}
                  rows="5"
                />
              </div>
            </div>
          ))}

          <h3>Benefits</h3>
          {data.benefits.map((benefit, index) => (
            <div key={index} className="form-group">
              <label>Benefit {index + 1}</label>
              <input
                type="text"
                placeholder="Title"
                value={benefit.title}
                onChange={(e) => {
                  const newBenefits = [...data.benefits];
                  newBenefits[index] = {
                    ...newBenefits[index],
                    title: e.target.value
                  };
                  onUpdate({ ...data, benefits: newBenefits });
                }}
              />
              <textarea
                placeholder="Description"
                value={benefit.description}
                onChange={(e) => {
                  const newBenefits = [...data.benefits];
                  newBenefits[index] = {
                    ...newBenefits[index],
                    description: e.target.value
                  };
                  onUpdate({ ...data, benefits: newBenefits });
                }}
                rows="3"
              />
            </div>
          ))}

          <h3>Contact Information</h3>
          <div className="form-group">
            <label>Email</label>
            <input
              type="email"
              value={data.contact.email}
              onChange={(e) => onUpdate({
                ...data,
                contact: { ...data.contact, email: e.target.value }
              })}
            />
          </div>
          <div className="form-group">
            <label>Phone</label>
            <input
              type="text"
              value={data.contact.phone}
              onChange={(e) => onUpdate({
                ...data,
                contact: { ...data.contact, phone: e.target.value }
              })}
            />
          </div>
        </div>
      </CollapsibleSection>
    </div>
  );
};

const GstEditor = ({ data, onUpdate }) => {
  if (!data) return <div>Loading GST data...</div>;

  const updateHero = (field, value) => {
    onUpdate({
      ...data,
      hero: { ...data.hero, [field]: value }
    });
  };

  const updateService = (type, field, value) => {
    onUpdate({
      ...data,
      services: {
        ...data.services,
        [type]: {
          ...data.services[type],
          [field]: field === 'features' ? value.split('\n') : value
        }
      }
    });
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...data.benefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    onUpdate({ ...data, benefits: newBenefits });
  };

  const updateContact = (field, value) => {
    onUpdate({
      ...data,
      contact: { ...data.contact, [field]: value }
    });
  };

  return (
    <CollapsibleSection title="GST Page Content">
      <div className="editor-section">
        <h3>Hero Section</h3>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.hero.title}
            onChange={(e) => updateHero('title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <input
            type="text"
            value={data.hero.subtitle}
            onChange={(e) => updateHero('subtitle', e.target.value)}
          />
        </div>

        <h3>Service Plans</h3>
        <div className="service-category">
          <h4>Standard Plan</h4>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.services.standard.title}
              onChange={(e) => updateService('standard', 'title', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Price</label>
            <input
              type="text"
              value={data.services.standard.price}
              onChange={(e) => updateService('standard', 'price', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Features (one per line)</label>
            <textarea
              value={data.services.standard.features.join('\n')}
              onChange={(e) => updateService('standard', 'features', e.target.value)}
              rows="5"
            />
          </div>
        </div>

        <div className="service-category">
          <h4>Premium Plan</h4>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.services.premium.title}
              onChange={(e) => updateService('premium', 'title', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Price</label>
            <input
              type="text"
              value={data.services.premium.price}
              onChange={(e) => updateService('premium', 'price', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Features (one per line)</label>
            <textarea
              value={data.services.premium.features.join('\n')}
              onChange={(e) => updateService('premium', 'features', e.target.value)}
              rows="5"
            />
          </div>
        </div>

        <h3>Benefits</h3>
        {data.benefits.map((benefit, index) => (
          <div key={index} className="form-group">
            <label>Benefit {index + 1}</label>
            <input
              type="text"
              value={benefit.title}
              onChange={(e) => updateBenefit(index, 'title', e.target.value)}
              placeholder="Title"
            />
            <input
              type="text"
              value={benefit.description}
              onChange={(e) => updateBenefit(index, 'description', e.target.value)}
              placeholder="Description"
            />
          </div>
        ))}

        <h3>Contact Information</h3>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={data.contact.email}
            onChange={(e) => updateContact('email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="text"
            value={data.contact.phone}
            onChange={(e) => updateContact('phone', e.target.value)}
          />
        </div>
      </div>
    </CollapsibleSection>
  );
};

const AdvisoryEditor = ({ data, onUpdate }) => {
  if (!data) return <div>Loading advisory data...</div>;

  const updateHero = (field, value) => {
    onUpdate({
      ...data,
      hero: { ...data.hero, [field]: value }
    });
  };

  const updateService = (type, field, value) => {
    onUpdate({
      ...data,
      services: {
        ...data.services,
        [type]: {
          ...data.services[type],
          [field]: field === 'features' ? value.split('\n') : value
        }
      }
    });
  };

  const updateBenefit = (index, field, value) => {
    const newBenefits = [...data.benefits];
    newBenefits[index] = { ...newBenefits[index], [field]: value };
    onUpdate({ ...data, benefits: newBenefits });
  };

  const updateContact = (field, value) => {
    onUpdate({
      ...data,
      contact: { ...data.contact, [field]: value }
    });
  };

  return (
    <CollapsibleSection title="Advisory Page Content">
      <div className="editor-section">
        <h3>Hero Section</h3>
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.hero.title}
            onChange={(e) => updateHero('title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <input
            type="text"
            value={data.hero.subtitle}
            onChange={(e) => updateHero('subtitle', e.target.value)}
          />
        </div>

        <h3>Service Plans</h3>
        <div className="service-category">
          <h4>Standard Plan</h4>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.services.standard.title}
              onChange={(e) => updateService('standard', 'title', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Price</label>
            <input
              type="text"
              value={data.services.standard.price}
              onChange={(e) => updateService('standard', 'price', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Features (one per line)</label>
            <textarea
              value={data.services.standard.features.join('\n')}
              onChange={(e) => updateService('standard', 'features', e.target.value)}
              rows="5"
            />
          </div>
        </div>

        <div className="service-category">
          <h4>Premium Plan</h4>
          <div className="form-group">
            <label>Title</label>
            <input
              type="text"
              value={data.services.premium.title}
              onChange={(e) => updateService('premium', 'title', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Price</label>
            <input
              type="text"
              value={data.services.premium.price}
              onChange={(e) => updateService('premium', 'price', e.target.value)}
            />
          </div>
          <div className="form-group">
            <label>Features (one per line)</label>
            <textarea
              value={data.services.premium.features.join('\n')}
              onChange={(e) => updateService('premium', 'features', e.target.value)}
              rows="5"
            />
          </div>
        </div>

        <h3>Benefits</h3>
        {data.benefits.map((benefit, index) => (
          <div key={index} className="form-group">
            <label>Benefit {index + 1}</label>
            <input
              type="text"
              value={benefit.title}
              onChange={(e) => updateBenefit(index, 'title', e.target.value)}
              placeholder="Title"
            />
            <input
              type="text"
              value={benefit.description}
              onChange={(e) => updateBenefit(index, 'description', e.target.value)}
              placeholder="Description"
            />
          </div>
        ))}

        <h3>Contact Information</h3>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={data.contact.email}
            onChange={(e) => updateContact('email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="text"
            value={data.contact.phone}
            onChange={(e) => updateContact('phone', e.target.value)}
          />
        </div>
      </div>
    </CollapsibleSection>
  );
};

const FinancialEditor = ({ data, onUpdate }) => {
  if (!data) return <div>Loading financial data...</div>;

  const updateHero = (field, value) => {
    const updatedData = { ...data };
    updatedData.hero[field] = value;
    onUpdate(updatedData);
  };

  const updateService = (type, field, value) => {
    const updatedData = { ...data };
    if (field === 'features') {
      const featuresArray = value.split('\n').filter(item => item.trim() !== '');
      updatedData.services[type].features = featuresArray;
    } else {
      updatedData.services[type][field] = value;
    }
    onUpdate(updatedData);
  };

  const updateBenefit = (index, field, value) => {
    const updatedData = { ...data };
    updatedData.benefits[index][field] = value;
    onUpdate(updatedData);
  };

  const updateContact = (field, value) => {
    const updatedData = { ...data };
    updatedData.contact[field] = value;
    onUpdate(updatedData);
  };

  return (
    <div className="editor-section">
      <h3>Financial Reporting Page</h3>
      
      <CollapsibleSection title="Hero Section">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.hero.title}
            onChange={(e) => updateHero('title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <input
            type="text"
            value={data.hero.subtitle}
            onChange={(e) => updateHero('subtitle', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Standard Service Plan">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.services.standard.title}
            onChange={(e) => updateService('standard', 'title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Price</label>
          <input
            type="text"
            value={data.services.standard.price}
            onChange={(e) => updateService('standard', 'price', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Features (one per line)</label>
          <textarea
            value={data.services.standard.features.join('\n')}
            onChange={(e) => updateService('standard', 'features', e.target.value)}
            rows={5}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Premium Service Plan">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.services.premium.title}
            onChange={(e) => updateService('premium', 'title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Price</label>
          <input
            type="text"
            value={data.services.premium.price}
            onChange={(e) => updateService('premium', 'price', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Features (one per line)</label>
          <textarea
            value={data.services.premium.features.join('\n')}
            onChange={(e) => updateService('premium', 'features', e.target.value)}
            rows={5}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Benefits">
        {data.benefits.map((benefit, index) => (
          <div key={index} className="service-item">
            <h4>Benefit {index + 1}</h4>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={benefit.title}
                onChange={(e) => updateBenefit(index, 'title', e.target.value)}
              />
            </div>
            <div className="form-group">
              <label>Description</label>
              <textarea
                value={benefit.description}
                onChange={(e) => updateBenefit(index, 'description', e.target.value)}
                rows={3}
              />
            </div>
          </div>
        ))}
      </CollapsibleSection>

      <CollapsibleSection title="Contact Information">
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={data.contact.email}
            onChange={(e) => updateContact('email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Phone</label>
          <input
            type="text"
            value={data.contact.phone}
            onChange={(e) => updateContact('phone', e.target.value)}
          />
        </div>
      </CollapsibleSection>
    </div>
  );
};

const CheckoutEditor = ({ data, onUpdate }) => {
  if (!data) return <div>Loading checkout data...</div>;

  const updateHero = (field, value) => {
    onUpdate({
      ...data,
      hero: { ...data.hero, [field]: value }
    });
  };

  const updatePaymentInfo = (field, value) => {
    onUpdate({
      ...data,
      payment_info: { ...data.payment_info, [field]: value }
    });
  };

  const updateFormField = (field, value) => {
    onUpdate({
      ...data,
      form_fields: { ...data.form_fields, [field]: value }
    });
  };

  const updateButton = (field, value) => {
    onUpdate({
      ...data,
      buttons: { ...data.buttons, [field]: value }
    });
  };

  return (
    <div className="content-editor">
      <CollapsibleSection title="Hero Section">
        <div className="form-group">
          <label>Title</label>
          <input
            type="text"
            value={data.hero.title}
            onChange={(e) => updateHero('title', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Subtitle</label>
          <input
            type="text"
            value={data.hero.subtitle}
            onChange={(e) => updateHero('subtitle', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Description</label>
          <textarea
            value={data.hero.description}
            onChange={(e) => updateHero('description', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Payment Information">
        <div className="form-group">
          <label>Phone Number</label>
          <input
            type="text"
            value={data.payment_info.phone}
            onChange={(e) => updatePaymentInfo('phone', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Email</label>
          <input
            type="email"
            value={data.payment_info.email}
            onChange={(e) => updatePaymentInfo('email', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Instructions</label>
          <textarea
            value={data.payment_info.instructions}
            onChange={(e) => updatePaymentInfo('instructions', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Thank You Message</label>
          <textarea
            value={data.payment_info.thank_you_message}
            onChange={(e) => updatePaymentInfo('thank_you_message', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Form Fields">
        <div className="form-group">
          <label>Name Label</label>
          <input
            type="text"
            value={data.form_fields.name_label}
            onChange={(e) => updateFormField('name_label', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Email Label</label>
          <input
            type="text"
            value={data.form_fields.email_label}
            onChange={(e) => updateFormField('email_label', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Phone Label</label>
          <input
            type="text"
            value={data.form_fields.phone_label}
            onChange={(e) => updateFormField('phone_label', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Address Label</label>
          <input
            type="text"
            value={data.form_fields.address_label}
            onChange={(e) => updateFormField('address_label', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>City Label</label>
          <input
            type="text"
            value={data.form_fields.city_label}
            onChange={(e) => updateFormField('city_label', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>State Label</label>
          <input
            type="text"
            value={data.form_fields.state_label}
            onChange={(e) => updateFormField('state_label', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>ZIP Code Label</label>
          <input
            type="text"
            value={data.form_fields.zip_label}
            onChange={(e) => updateFormField('zip_label', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Country Label</label>
          <input
            type="text"
            value={data.form_fields.country_label}
            onChange={(e) => updateFormField('country_label', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Notes Label</label>
          <input
            type="text"
            value={data.form_fields.notes_label}
            onChange={(e) => updateFormField('notes_label', e.target.value)}
          />
        </div>
      </CollapsibleSection>

      <CollapsibleSection title="Buttons">
        <div className="form-group">
          <label>Submit Button</label>
          <input
            type="text"
            value={data.buttons.submit}
            onChange={(e) => updateButton('submit', e.target.value)}
          />
        </div>
        <div className="form-group">
          <label>Back Button</label>
          <input
            type="text"
            value={data.buttons.back}
            onChange={(e) => updateButton('back', e.target.value)}
          />
        </div>
      </CollapsibleSection>
    </div>
  );
};

function Admin() {
  const [pages, setPages] = useState({
    home: null,
    accounting: null,
    registration: null,
    tax: null,
    about: null,
    contact: null,
    gst: null,
    advisory: null,
    financial: null,
    checkout: null
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecked, setAuthChecked] = useState(false);

  // Check if there's a valid token
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('adminToken');
      const expiry = localStorage.getItem('adminTokenExpiry');
      
      if (token && expiry) {
        // Check if token is expired
        const now = Math.floor(Date.now() / 1000);
        if (parseInt(expiry) > now) {
          // Set the token in axios headers
          axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
          
          try {
            // Verify the token with the backend
            const response = await axios.get(`${process.env.REACT_APP_API_URL}/api/admin/verify`);
            if (response.data.authenticated) {
              setIsAuthenticated(true);
            } else {
              // Token is invalid or expired
              localStorage.removeItem('adminToken');
              localStorage.removeItem('adminTokenExpiry');
            }
          } catch (err) {
            console.error('Auth verification error:', err);
            localStorage.removeItem('adminToken');
            localStorage.removeItem('adminTokenExpiry');
          }
        } else {
          // Token is expired
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminTokenExpiry');
        }
      }
      
      setAuthChecked(true);
    };
    
    checkAuth();
  }, []);

  // Only fetch data if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      fetchData();
    } else if (authChecked) {
      setLoading(false);
    }
  }, [isAuthenticated, authChecked]);

    const fetchData = async () => {
      setLoading(true);
    setError(null); // Clear any previous errors
    
    try {
      // Add a delay to ensure token is properly set in axios headers
      await new Promise(resolve => setTimeout(resolve, 100));
      
      // Use Promise.all to fetch all data in parallel
      const [
        homeResponse,
        accountingResponse,
        registrationResponse,
        taxResponse,
        aboutResponse,
        contactResponse,
        gstResponse,
        advisoryResponse,
        financialResponse,
        checkoutResponse
      ] = await Promise.all([
        axios.get(`${process.env.REACT_APP_API_URL}/api/home`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/accounting/services`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/registration`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/tax`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/about`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/contact`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/gst`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/advisory`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/financial`),
        axios.get(`${process.env.REACT_APP_API_URL}/api/checkout`)
      ]);

        setPages({
          home: homeResponse.data,
          accounting: accountingResponse.data,
          registration: registrationResponse.data,
          tax: taxResponse.data,
          about: aboutResponse.data,
          contact: contactResponse.data,
          gst: gstResponse.data,
          advisory: advisoryResponse.data,
        financial: financialResponse.data,
        checkout: checkoutResponse.data
        });
      
        setLoading(false);
      } catch (error) {
        console.error('Error fetching data:', error);
      
      // Check if it's an authentication error
      if (error.response && error.response.status === 401) {
        // Token is invalid or expired
        localStorage.removeItem('adminToken');
        localStorage.removeItem('adminTokenExpiry');
        setIsAuthenticated(false);
        setError('Your session has expired. Please log in again.');
      } else {
        setError('Failed to load data. Please try again.');
      }
      
        setLoading(false);
      }
    };

  // Add this validation function before the handleSave function
  const validateData = (page, data) => {
    // Check if data is empty
    if (!data || Object.keys(data).length === 0) {
      return { valid: false, error: "Data is empty" };
    }
    
    // Basic validation based on page type
    switch(page) {
      case 'home':
        if (!data.hero || !data.services || !data.contact) {
          return { valid: false, error: "Home page data is missing required sections" };
        }
        break;
        
      case 'accounting':
        if (!data.hero || !data.services || !data.benefits) {
          return { valid: false, error: "Accounting page data is missing required sections" };
        }
        break;
        
      case 'registration':
        if (!data.hero || !data.services || !data.benefits) {
          return { valid: false, error: "Registration page data is missing required sections" };
        }
        break;
        
      case 'tax':
        if (!data.hero || !data.services) {
          return { valid: false, error: "Tax page data is missing required sections" };
        }
        break;
        
      case 'gst':
        if (!data.hero || !data.services || !data.benefits) {
          return { valid: false, error: "GST page data is missing required sections" };
        }
        break;
        
      case 'advisory':
        if (!data.hero || !data.services || !data.benefits) {
          return { valid: false, error: "Advisory page data is missing required sections" };
        }
        break;
        
      case 'financial':
        if (!data.hero || !data.services || !data.benefits) {
          return { valid: false, error: "Financial page data is missing required sections" };
        }
        break;
        
      case 'about':
        if (!data.hero || !data.team || !data.values) {
          return { valid: false, error: "About page data is missing required sections" };
        }
        break;
        
      case 'contact':
        if (!data.hero || !data.contact_info) {
          return { valid: false, error: "Contact page data is missing required sections" };
        }
        break;
        
      case 'checkout':
        if (!data.hero || !data.payment_info || !data.form_fields || !data.buttons) {
          return { valid: false, error: "Checkout page data is missing required sections" };
        }
        break;
        
      default:
        return { valid: false, error: `Unknown page type: ${page}` };
    }
    
    return { valid: true };
  };

  const handleSave = async (page) => {
    try {
      // Validate data before sending
      const validation = validateData(page, pages[page]);
      if (!validation.valid) {
        alert(`Validation Error: ${validation.error}`);
        return;
      }
      
      // Set loading state for the current page
      setLoading(true);
      
      // Get API key from .env - this is already used by axios default headers if adminToken is set
      const apiKey = process.env.REACT_APP_API_KEY;
      
      // Make sure we have a valid token in headers
      const token = localStorage.getItem('adminToken');
      if (!token) {
        throw new Error('No authentication token found');
      }
      
      // Make the request with the API key and admin token for double security
      const response = await axios.post(
        `${process.env.REACT_APP_API_URL}/api/update/${page}`, 
        pages[page],
        {
          headers: {
            'X-API-Key': apiKey,
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          }
        }
      );
      
      // Log the successful response
      console.log('Server response:', response.data);
      
      console.log(`${page} data updated successfully!`);
      alert(`${page} data updated successfully!`);
    } catch (err) {
      console.error('Error updating data:', {
        message: err.message,
        status: err.response?.status,
        statusText: err.response?.statusText,
        responseData: err.response?.data
      });
      
      let errorMessage = `Error updating ${page} data`;
      
      // Provide more specific error messages based on status codes
      if (err.response) {
        if (err.response.status === 403) {
          errorMessage += ": Invalid or missing API key";
        } else if (err.response.status === 401) {
          errorMessage += ": Authentication failed";
          // If authentication failed, redirect to login by clearing the token
          setIsAuthenticated(false);
          localStorage.removeItem('adminToken');
          localStorage.removeItem('adminTokenExpiry');
          delete axios.defaults.headers.common['Authorization'];
        } else if (err.response.data && err.response.data.detail) {
          errorMessage += `:\n\n${err.response.data.detail}`;
        }
      }
      
      alert(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/api/admin/logout`);
    } catch (err) {
      console.error('Logout error:', err);
    } finally {
      // Remove token from localStorage
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminTokenExpiry');
      
      // Remove token from axios headers
      delete axios.defaults.headers.common['Authorization'];
      
      // Update state
      setIsAuthenticated(false);
      
      // Clear loaded data for security
      setPages({
        home: null,
        accounting: null,
        registration: null,
        tax: null,
        about: null,
        contact: null,
        gst: null,
        advisory: null,
        financial: null,
        checkout: null
      });
    }
  };

  if (!authChecked) {
    return <div className="admin-loading">Checking authentication...</div>;
  }

  if (!isAuthenticated) {
    return <AdminLogin onLogin={setIsAuthenticated} />;
  }

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  if (error) {
    return <div className="admin-error">{error}</div>;
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
      <h1>Admin Dashboard</h1>
        <button className="logout-button" onClick={handleLogout}>Logout</button>
      </div>

      <CollapsibleSection title="Home">
        <HomeEditor data={pages.home} onUpdate={(newData) => setPages({ ...pages, home: newData })} />
        <button className="save-button" onClick={() => handleSave('home')}>Save Home</button>
      </CollapsibleSection>

      <CollapsibleSection title="Accounting">
        <AccountingEditor data={pages.accounting} onUpdate={(newData) => setPages({ ...pages, accounting: newData })} />
        <button className="save-button" onClick={() => handleSave('accounting')}>Save Accounting</button>
      </CollapsibleSection>

      <CollapsibleSection title="Registration">
        <RegistrationEditor data={pages.registration} onUpdate={(newData) => setPages({ ...pages, registration: newData })} />
        <button className="save-button" onClick={() => handleSave('registration')}>Save Registration</button>
      </CollapsibleSection>

      <CollapsibleSection title="Tax">
        <TaxEditor data={pages.tax} onUpdate={(newData) => setPages({ ...pages, tax: newData })} />
        <button className="save-button" onClick={() => handleSave('tax')}>Save Tax</button>
      </CollapsibleSection>

      <CollapsibleSection title="GST">
        <GstEditor data={pages.gst} onUpdate={(newData) => setPages({ ...pages, gst: newData })} />
        <button className="save-button" onClick={() => handleSave('gst')}>Save GST</button>
      </CollapsibleSection>

      <CollapsibleSection title="Advisory">
        <AdvisoryEditor data={pages.advisory} onUpdate={(newData) => setPages({ ...pages, advisory: newData })} />
        <button className="save-button" onClick={() => handleSave('advisory')}>Save Advisory</button>
      </CollapsibleSection>

      <CollapsibleSection title="About">
        <AboutEditor data={pages.about} onUpdate={(newData) => setPages({ ...pages, about: newData })} />
        <button className="save-button" onClick={() => handleSave('about')}>Save About</button>
      </CollapsibleSection>

      <CollapsibleSection title="Contact">
        <ContactEditor data={pages.contact} onUpdate={(newData) => setPages({ ...pages, contact: newData })} />
        <button className="save-button" onClick={() => handleSave('contact')}>Save Contact</button>
      </CollapsibleSection>

      <CollapsibleSection title="Financial">
        <FinancialEditor data={pages.financial} onUpdate={(newData) => setPages({ ...pages, financial: newData })} />
        <button className="save-button" onClick={() => handleSave('financial')}>Save Financial</button>
      </CollapsibleSection>

      <CollapsibleSection title="Checkout">
        <CheckoutEditor data={pages.checkout} onUpdate={(newData) => setPages({ ...pages, checkout: newData })} />
        <button className="save-button" onClick={() => handleSave('checkout')}>Save Checkout</button>
      </CollapsibleSection>
    </div>
  );
}

export default Admin; 